import axios from "axios";
import { LabelApi, labelApi, LabelConfigurationId } from "./labels";
import { priorityApi, PriorityApi, PriorityConfigurationId } from "./priorities";
import { resolutionApi, ResolutionApi, ResolutionConfigurationId } from "./resolutions";
import {
    StatusApi,
    statusApi,
    TicketStatusConfigurationId,
    TicketStatusMigration,
} from "./statuses";
import { ticketTypeApi, TicketTypeApi, TicketTypeConfigurationId } from "./types";
import { customDataConfigApi, CustomDataConfigApi, CustomDataConfigurationId } from "./customData";
import { roleApi, RoleApi, RoleConfigurationId, TicketStatusRoute } from "./roles";

export type ProjectConfigurationId = string & { __brand: "ProjectConfigurationId" };
export type ProjectConfiguration = {
    id: ProjectConfigurationId;
    name: string;
    resolutionConfigurationId: ResolutionConfigurationId;
    ticketStatusConfigurationId: TicketStatusConfigurationId;
    ticketTypeConfigurationId: TicketTypeConfigurationId;
    priorityConfigurationId: PriorityConfigurationId;
    labelConfigurationId: LabelConfigurationId;
    projectCustomDataConfigurationId: CustomDataConfigurationId | null;
    roleConfigurationId: RoleConfigurationId | null;
    statusRoutes: TicketStatusRoute[];
};

export type { LabelConfiguration, LabelConfigurationId } from "./labels";
export type { PriorityConfiguration, PriorityConfigurationId } from "./priorities";
export type { ResolutionConfiguration, ResolutionConfigurationId } from "./resolutions";
export type { TicketStatusConfiguration, TicketStatusConfigurationId } from "./statuses";
export type { TicketTypeConfiguration, TicketTypeConfigurationId } from "./types";
export type { RoleConfiguration, RoleConfigurationId, ProjectRole, ProjectRoleId } from "./roles";

type ProjectConfigApiBase = {
    getProjectConfigurations(): Promise<ProjectConfiguration[]>;
    createProjectConfiguration(
        name: string,
        labelConfigurationId: LabelConfigurationId,
        resolutionConfigurationId: ResolutionConfigurationId,
        ticketStatusConfigurationId: TicketStatusConfigurationId,
        ticketTypeConfigurationId: TicketTypeConfigurationId,
        priorityConfigurationId: PriorityConfigurationId
    ): Promise<ProjectConfiguration>;
    editProjectConfiguration(
        config: ProjectConfiguration,
        patch: ProjectConfiguration,
        migrationModel: TicketStatusMigration[]
    ): Promise<void>;
    deleteProjectConfiguration(config: ProjectConfigurationId): Promise<void>;
    setStatusRoutes(config: ProjectConfigurationId, routes: TicketStatusRoute[]): Promise<void>;
};

export type ProjectConfigApi = LabelApi &
    PriorityApi &
    ResolutionApi &
    StatusApi &
    TicketTypeApi &
    CustomDataConfigApi &
    RoleApi &
    ProjectConfigApiBase;

export const projectConfigApi: ProjectConfigApi = {
    async getProjectConfigurations() {
        const { data: configs } = await axios.get<ProjectConfiguration[]>(
            "/api/v0/project-configurations"
        );
        return configs;
    },
    async createProjectConfiguration(
        name,
        labelConfigurationId,
        resolutionConfigurationId,
        ticketStatusConfigurationId,
        ticketTypeConfigurationId,
        priorityConfigurationId
    ) {
        const { data: config } = await axios.post<ProjectConfiguration>(
            "/api/v0/project-configurations",
            {
                name,
                labelConfigurationId,
                resolutionConfigurationId,
                ticketStatusConfigurationId,
                ticketTypeConfigurationId,
                priorityConfigurationId,
            }
        );
        return config;
    },
    async editProjectConfiguration(config, patch, migrations = []) {
        // Diff only what the backend patch model has: callers hand in whole view objects,
        // and any stray key would become a patch op the server rejects.
        const edited = {
            ...config,
            name: patch.name,
            labelConfigurationId: patch.labelConfigurationId,
            resolutionConfigurationId: patch.resolutionConfigurationId,
            ticketStatusConfigurationId: patch.ticketStatusConfigurationId,
            ticketTypeConfigurationId: patch.ticketTypeConfigurationId,
            priorityConfigurationId: patch.priorityConfigurationId,
            projectCustomDataConfigurationId: patch.projectCustomDataConfigurationId,
            roleConfigurationId: patch.roleConfigurationId,
        };

        const { compare } = await import("fast-json-patch");

        const data = { patch: compare(config, edited), migrations };

        await axios.patch(`/api/v0/project-configurations/${config.id}`, data);
    },
    async deleteProjectConfiguration(config) {
        await axios.delete(`/api/v0/project-configurations/${config}`);
    },
    async setStatusRoutes(config, routes) {
        await axios.put(`/api/v0/project-configurations/${config}/routes`, routes);
    },

    ...labelApi,
    ...priorityApi,
    ...resolutionApi,
    ...statusApi,
    ...ticketTypeApi,
    ...customDataConfigApi,
    ...roleApi,
};
